import $ from "jquery"
import GroupWithUsers from "./GroupWithUsers"
import {cancelBubble, addClass} from "../../atBrowserUtils"

/**
 * Wrapper for GroupWithUsers. Contains all needed dom elements of user group row
 */
export default class GroupRow extends GroupWithUsers {
    /**
     * @param div user group row
     * @param deleteLink user group delete button
     * @param undoDeleteButton group undo delete button
     * @param acceptDeleteButton group accept delete button
     * @param undoEditButton user group undo edit button
     * @param acceptEditButton user group accept edit button
     * @param input user group edit input field
     * @param nameCell cell with user group name
     * @param inputCell cell with edit user group input field
     * @param editGroupCell cell for edited group table
     * @param waitingAnimationCell cell with waiting ajax animation
     * @param group GroupRow to wrap
     * @param validatorGroupStrategy
     * @param controller user group management controller
     */
    init(div, deleteLink, undoDeleteButton, acceptDeleteButton, undoEditButton, acceptEditButton, input, nameCell,
            inputCell, editGroupCell, waitingAnimationCell, group, validatorGroupStrategy, controller) {
        GroupWithUsers.call(this, group.id, group.name, validatorGroupStrategy, group.users);
        this.div = div;
        this.deleteLink = deleteLink;
        this.undoEditButton = undoEditButton;
        this.acceptEditButton = acceptEditButton;
        this.undoDeleteButton = undoDeleteButton;
        this.acceptDeleteButton = acceptDeleteButton;
        this.input = input;
        this.nameCell = nameCell;
        this.nameDiv = nameCell.firstChild.firstChild;
        this.inputCell = inputCell;
        this.editGroupCell = editGroupCell;
        this.waitingAnimationCell = waitingAnimationCell;
        this.generateEventHandlers(controller);
        this.enableControls();
        this.enableRow();

        return this;
    }

    /**
     * Generate all needed event handlers with user group row elements
     * @param controller user group management controller
     */
    generateEventHandlers(controller) {
        this.normalEditGroupOnClick = event => {
            cancelBubble(event);
            controller.groupEventHandler(
                    () => {
                        controller.startGroupEditing(this);
                        controller.groupSelected(this);
                    },
                    false);
        };

        this.normalDeleteGroupOnClick = event => {
            cancelBubble(event);
            controller.groupEventHandler(() => controller.confirmGroupDelete(this), false);
        };

        this.noFunctionWithCancelBubble = event => cancelBubble(event);

        this.rowOnClick = () => controller.groupEventHandler(() => controller.groupSelected(this), false);

        this.onClickOutsideEditGroupCell = () => {
            controller.hideErrors();
            controller.endGroupEditing(false);
        };

        this.inputOnClick = () => cancelBubble(event);

        this.undoEditButtonOnClick = event => {
            cancelBubble(event);
            controller.groupEventHandler(() => {}, false);
        };

        this.acceptEditButtonOnClick = event => {
            cancelBubble(event);
            controller.getGroupController().startNewGroupNameSaving(controller, this);
        };

        this.undoDeleteButtonOnClick = event => {
            cancelBubble(event);
            controller.groupEventHandler(() => {}, false);
        };

        this.acceptDeleteButtonOnClick = event => {
            cancelBubble(event);
            controller.groupEventHandler(() => controller.deleteGroup(this), false);
        };

        this.inputOnKeyDown = event => {
            cancelBubble(event);
            GroupManagementUtil.createEnterPressedHandler(event, () => controller.getGroupController().startNewGroupNameSaving(controller, this));
            GroupManagementUtil.createEscPressedHandler(event, () => controller.groupEventHandler(() => {}, false));
        };

        this.divOnMouseOver = () => {
            controller.getHighlightController().highlightHoveredGroup(this);
            this.hovered = true;
        };

        this.divOnMouseOut = () => {
            controller.getHighlightController().unhighlightHoveredGroup(this);
            this.hovered = false;
        };

        this.groupButtonControlOnMouseOver = function() {
            controller.getHighlightController().highlightHoveredControl(this);
        };

        this.groupButtonControlOnMouseOut = function() {
            controller.getHighlightController().unhighlightHoveredControl(this);
        };

        this.groupUndoAcceptEditButtonOnMouseOver = function() {
            controller.getHighlightController().highlightHoveredUndoAcceptEditButton(this);
        };

        this.groupUndoAcceptEditButtonOnMouseOut = function() {
            controller.getHighlightController().unhighlightHoveredUndoAcceptEditButton(this);
        };

        this.noFunction = () => false
    }

    /**
     * Disable all needed controls when ajax request is processed with this row
     */
    disableControlsDueToAjax() {
        if (!this.validatorGroupStrategy.isNullGroup(this)) {
            this.nameDiv.onclick = this.noFunction;
            this.deleteLink.onclick = this.noFunctionWithCancelBubble;
            this.deleteLink.onmouseover = this.noFunction;
            this.deleteLink.onmouseout = this.noFunction;
        }
    }

    /**
     * Enable all controls. Used to initialize event handlers
     */
    enableControls() {
        this.div.onmouseover = this.divOnMouseOver;
        this.div.onmouseout = this.divOnMouseOut;
        if (!this.validatorGroupStrategy.isNullGroup(this)) {
            this.nameDiv.onclick = this.normalEditGroupOnClick;
            this.undoEditButton.onclick = this.undoEditButtonOnClick;
            this.acceptEditButton.onclick = this.acceptEditButtonOnClick;
            this.undoEditButton.onmouseover = this.groupUndoAcceptEditButtonOnMouseOver;
            this.undoEditButton.onmouseout = this.groupUndoAcceptEditButtonOnMouseOut;
            this.acceptEditButton.onmouseover = this.groupUndoAcceptEditButtonOnMouseOver;
            this.acceptEditButton.onmouseout = this.groupUndoAcceptEditButtonOnMouseOut;
            this.undoDeleteButton.onclick = this.undoDeleteButtonOnClick;
            this.acceptDeleteButton.onclick = this.acceptDeleteButtonOnClick;
            this.input.onkeydown = this.inputOnKeyDown;
        }
        if (!this.validatorGroupStrategy.isNullGroup(this) && !this.validatorGroupStrategy.isDefaultGroup(this)) {
            this.deleteLink.onclick = this.normalDeleteGroupOnClick;
            this.deleteLink.onmouseover = this.groupButtonControlOnMouseOver;
            this.deleteLink.onmouseout = this.groupButtonControlOnMouseOut;
        }
    }

    /**
     * Disable mouse events when they are not needed
     */
    disableMouseEvents(saveHover) {
        if (!saveHover) {
            this.div.onmouseover = this.noFunction;
            this.div.onmouseout = this.noFunction;
        }
        if (!this.validatorGroupStrategy.isNullGroup(this) && !this.validatorGroupStrategy.isDefaultGroup(this)) {
            this.deleteLink.onmouseover = this.noFunction;
            this.deleteLink.onmouseout = this.noFunction;
        }
    }

    /**
     * Disable mouse events on edited user group when they are not needed
     */
    disableEditedGroupMouseEvents() {
        if (!this.validatorGroupStrategy.isNullGroup(this) && !this.validatorGroupStrategy.isDefaultGroup(this)) {
            this.undoEditButton.onmouseover = this.noFunction;
            this.acceptEditButton.onmouseover = this.noFunction;
            this.undoEditButton.onmouseout = this.noFunction;
            this.acceptEditButton.onmouseout = this.noFunction;
        }
    }

    /**
     * Enable mouse events on edited user group
     */
    enableEditedGroupMouseEvents() {
        if (!this.validatorGroupStrategy.isNullGroup(this) && !this.validatorGroupStrategy.isDefaultGroup(this)) {
            this.undoEditButton.onmouseover = this.groupUndoAcceptEditButtonOnMouseOver;
            this.acceptEditButton.onmouseover = this.groupUndoAcceptEditButtonOnMouseOver;
            this.undoEditButton.onmouseout = this.groupUndoAcceptEditButtonOnMouseOut;
            this.acceptEditButton.onmouseout = this.groupUndoAcceptEditButtonOnMouseOut;
        }
    }


    /**
     * Enable mouse events on user group
     */
    enableMouseEvents() {
        this.div.onmouseover = this.divOnMouseOver;
        this.div.onmouseout = this.divOnMouseOut;
        if (!this.validatorGroupStrategy.isNullGroup(this) && !this.validatorGroupStrategy.isDefaultGroup(this)) {
            this.deleteLink.onmouseover = this.groupButtonControlOnMouseOver;
            this.deleteLink.onmouseout = this.groupButtonControlOnMouseOut;
        }
    }

    /**
     * Disable mouse events on whole user group row
     */
    disableRow() {
        this.div.onclick = this.noFunction;
    }

    /**
     * Enable mouse events on whole user group row
     */
    enableRow() {
        this.div.onclick = this.rowOnClick;
        this.input.onclick = this.noFunction;
        $(document).off("mousedown.editGroupCell");
    }

    /**
     * Disable all previous mouse events on whole row while group editing and set handler for cancel editing
     * Set handler on active input for prevent cancel editing while click on input field and handler for cancel editing while clicking outside name input
     */
    disableRowForEditing() {
        const context = this;
        const editGroupCell = $(this.editGroupCell);
        $(document).on("mousedown.editGroupCell", function(event) {
            const target = $(event.target);
            if (target.parents().index(editGroupCell) == -1) {
                context.onClickOutsideEditGroupCell();
            }
        });
        this.editGroupCell.onclick = this.inputOnClick;
    }

    /**
     * Pass user group name into text of user group edit input field
     */
    revertInputText() {
        this.input.value = this.name;
    }

    /**
     * Defines is user group name changed or not
     */
    isNameChanged() {
        return this.input.value.trim() != this.name.trim();
    }

    /**
     * Repaint row if ajax request is processed now onto it
     * @param additionalClassName
     */
    markAsAjaxProcessedNow(additionalClassName) {
        this.waitingAnimationCell.style.display = 'table-cell';
        addClass(this.div, additionalClassName);
    }

    isHovered() {
        return this.hovered;
    }

    focusAcceptDeleteButton() {
        $(this.acceptDeleteButton).focus();
    }
}


// WEBPACK FOOTER //
// ./web/js/groupManagement/entities/GroupRow.js